import {messagesService} from "@dv5/api"
import {
    CanvasContent,
    DiagramType,
    DiagramWidgetData,
    HorizontalTextAlign,
    isWidgetTypeEditable,
    MarkdownWidgetData,
    ReactionWidgetData,
    ShapeType,
    ShapeLineStyle,
    ShapeWidgetData,
    StickyNoteData, TableTheme, TableWidgetData, Template, TemplateItemParams,
    TextWidgetData,
    VerticalTextAlign,
    Widget,
    WidgetType
} from '@dv5/domain'
import {CanvasMode} from "../model/CanvasMode"
import {CanvasPosition} from "../model/CanvasPosition"
import {CreateWidget} from "../model/CreateWidget"
import {RemoveWidgets} from "../model/RemoveWidget"
import {roundToGrid} from "./gridUtils"

export interface CreateWidgets {
    canvasId: string
    widgets: Array<{
        type: WidgetType
        left: number
        top: number
        width: number
        height: number
        zIndex: number
        data: StickyNoteData | TextWidgetData | ShapeWidgetData | ReactionWidgetData | MarkdownWidgetData | DiagramWidgetData | TableWidgetData | import('@dv5/domain').DocumentWidgetData | import('@dv5/domain').TaskWidgetData
        layers: string
        tags: string
    }>
}

export interface RemoveWidgetLinks {
    canvasId: string
    linkIds: string[]
}

interface CreateWidgetFromTemplate {
    canvasId: string
    templateId: string
    layers: string
    leftDiff: number
    topDiff: number
    params: TemplateItemParams
}

class CanvasContentService {
    processClick(mode: CanvasMode,
                 position: CanvasPosition,
                 ctrlKey: boolean,
                 canvasId: string,
                 canvasSelection: Array<string>,
                 canvasContent: CanvasContent,
                 layerIds: Array<string>,
                 onChangeSelection: (selection: Array<string>) => void,
                 onStartEdit: (widgetId: string) => void,
                 onChangeTool: (newTool: CanvasMode) => void,
                 alignToGrid = false,
    ) {
        if (mode === "sticky-notes") {
            this.createStickyNote(position, canvasId, ctrlKey, layerIds.join(','), '', onChangeTool, alignToGrid)
        } else if (mode === "text-widget") {
            this.createTextWidget(position, canvasId, ctrlKey, layerIds.join(','), '', onChangeTool, alignToGrid)
        } else if (mode === "shape") {
            this.createShapeWidget(position, canvasId, ctrlKey, layerIds.join(','), '', onChangeTool, alignToGrid)
        } else if (mode === "reaction") {
            this.createReactionWidget(position, canvasId, ctrlKey, layerIds.join(','), '', onChangeTool, alignToGrid)
        } else if (mode === "markdown") {
            this.createMarkdownWidget(position, canvasId, ctrlKey, layerIds.join(','), '', onChangeTool, alignToGrid)
        } else if (mode === "diagram") {
            this.createDiagramWidget(position, canvasId, ctrlKey, layerIds.join(','), '', onChangeTool, alignToGrid)
        } else if (mode === "table") {
            this.createTableWidget(position, canvasId, ctrlKey, layerIds.join(','), '', onChangeTool, alignToGrid)
        } else if (mode === "select") {
            this.changeSelection(position, ctrlKey, canvasSelection, canvasContent, layerIds, onChangeSelection, onStartEdit)
        }
    }

    createStickyNote(position: CanvasPosition,
                     canvasId: string,
                     ctrlKey: boolean,
                     layers: string,
                     tags: string,
                     onChangeTool: (newTool: CanvasMode) => void,
                     alignToGrid = false,
    ) {
        messagesService.send<CreateWidget>('canvas.create-widget', {
            canvasId: canvasId,
            type: WidgetType.STICKY_NOTE,
            left: alignToGrid ? roundToGrid(position.left) : position.left,
            top: alignToGrid ? roundToGrid(position.top) : position.top,
            width: 64,
            height: 64,
            zIndex: 0,
            data: {
                text: 'Note',
                bgColor: '#FCCB1A',
                fontStyle: '',
                reactionType: ''
            },
            layers: layers,
            tags: tags,
        })
        if (!ctrlKey) {
            onChangeTool('select')
        }
    }

    createTextWidget(position: CanvasPosition,
                     canvasId: string,
                     ctrlKey: boolean,
                     layers: string,
                     tags: string,
                     onChangeTool: (newTool: CanvasMode) => void,
                     alignToGrid = false,
    ) {
        messagesService.send<CreateWidget>('canvas.create-widget', {
            canvasId: canvasId,
            type: WidgetType.TEXT_WIDGET,
            left: alignToGrid ? roundToGrid(position.left) : position.left,
            top: alignToGrid ? roundToGrid(position.top) : position.top,
            width: 200,
            height: 100,
            zIndex: 0,
            data: {
                text: 'Text',
                bgColor: 'transparent',
                fgColor: null,
                textProperties: {
                    horizontalTextAlign: HorizontalTextAlign.CENTER,
                    verticalTextAlign: VerticalTextAlign.CENTER,
                    fontSize: 12,
                    bold: false,
                    italic: false,
                    underline: false,
                    lineThrough: false,
                }
            } as TextWidgetData,
            layers: layers,
            tags: tags,
        })
        if (!ctrlKey) {
            onChangeTool('select')
        }
    }

    createReactionWidget(position: CanvasPosition,
                         canvasId: string,
                         ctrlKey: boolean,
                         layers: string,
                         tags: string,
                         onChangeTool: (newTool: CanvasMode) => void,
                         alignToGrid = false,
    ) {
        messagesService.send<CreateWidget>('canvas.create-widget', {
            canvasId: canvasId,
            type: WidgetType.REACTION,
            left: alignToGrid ? roundToGrid(position.left) : position.left,
            top: alignToGrid ? roundToGrid(position.top) : position.top,
            width: 48,
            height: 48,
            zIndex: 0,
            data: {
                reactionType: 'smile'
            } as ReactionWidgetData,
            layers: layers,
            tags: tags,
        })
        if (!ctrlKey) {
            onChangeTool('select')
        }
    }

    createShapeWidget(position: CanvasPosition,
                       canvasId: string,
                      ctrlKey: boolean,
                      layers: string,
                      tags: string,
                      onChangeTool: (newTool: CanvasMode) => void,
                      alignToGrid = false,
    ) {
        messagesService.send<CreateWidget>('canvas.create-widget', {
            canvasId: canvasId,
            type: WidgetType.SHAPE,
            left: alignToGrid ? roundToGrid(position.left) : position.left,
            top: alignToGrid ? roundToGrid(position.top) : position.top,
            width: 120,
            height: 120,
            zIndex: 0,
            data: {
                shapeType: ShapeType.RECTANGLE,
                lineStyle: ShapeLineStyle.SOLID,
                bgColor: '#4A90D9',
                borderColor: '#2C5F8A',
                fgColor: '#333333',
                borderWidth: 2,
                text: '',
            } as ShapeWidgetData,
            layers: layers,
            tags: tags,
        })
        if (!ctrlKey) {
            onChangeTool('select')
        }
    }

    createMarkdownWidget(position: CanvasPosition,
                         canvasId: string,
                         ctrlKey: boolean,
                         layers: string,
                         tags: string,
                         onChangeTool: (newTool: CanvasMode) => void,
                         alignToGrid = false,
    ) {
        messagesService.send<CreateWidget>('canvas.create-widget', {
            canvasId: canvasId,
            type: WidgetType.MARKDOWN,
            left: alignToGrid ? roundToGrid(position.left) : position.left,
            top: alignToGrid ? roundToGrid(position.top) : position.top,
            width: 300,
            height: 200,
            zIndex: 0,
            data: {
                text: '# Markdown\n\nWrite your *markdown* here.',
                compact: false,
            } as MarkdownWidgetData,
            layers: layers,
            tags: tags,
        })
        if (!ctrlKey) {
            onChangeTool('select')
        }
    }

    createDiagramWidget(position: CanvasPosition,
                        canvasId: string,
                        ctrlKey: boolean,
                        layers: string,
                        tags: string,
                        onChangeTool: (newTool: CanvasMode) => void,
                        alignToGrid = false,
    ) {
        messagesService.send<CreateWidget>('canvas.create-widget', {
            canvasId: canvasId,
            type: WidgetType.DIAGRAM,
            left: alignToGrid ? roundToGrid(position.left) : position.left,
            top: alignToGrid ? roundToGrid(position.top) : position.top,
            width: 400,
            height: 300,
            zIndex: 0,
            data: {
                text: '@startuml\na --> b\n@enduml',
                diagramType: DiagramType.PLANTUML,
                hideType: true,
            } as DiagramWidgetData,
            layers: layers,
            tags: tags,
        })
        if (!ctrlKey) {
            onChangeTool('select')
        }
    }

    createTableWidget(position: CanvasPosition,
                      canvasId: string,
                      ctrlKey: boolean,
                      layers: string,
                      tags: string,
                      onChangeTool: (newTool: CanvasMode) => void,
                      alignToGrid = false,
    ) {
        messagesService.send<CreateWidget>('canvas.create-widget', {
            canvasId: canvasId,
            type: WidgetType.TABLE,
            left: alignToGrid ? roundToGrid(position.left) : position.left,
            top: alignToGrid ? roundToGrid(position.top) : position.top,
            width: 400,
            height: 300,
            zIndex: 0,
            data: {
                text: 'Name,Role,Team\nAlice,Engineer,Core\nBob,Designer,"Core, Platform"',
                theme: TableTheme.LIGHT,
            } as TableWidgetData,
            layers: layers,
            tags: tags,
        })
        if (!ctrlKey) {
            onChangeTool('select')
        }
    }

    changeSelection(position: CanvasPosition,
                    ctrlKey: boolean,
                    canvasSelection: Array<string>,
                    canvasContent: CanvasContent,
                    layerIds: Array<string>,
                    onChangeSelection: (selection: Array<string>) => void,
                    onStartEdit: (widgetId: string) => void) {
        const widgetUnderPosition = this.isWidgetUnderPosition(canvasContent.widgets, position, layerIds)
        if (widgetUnderPosition) {
            if (!ctrlKey) {
                if (canvasSelection.length === 1
                    && canvasSelection[0] === widgetUnderPosition.id
                    && isWidgetTypeEditable(widgetUnderPosition.type)
                    && !widgetUnderPosition.locked) {
                    onStartEdit(widgetUnderPosition.id)
                } else {
                    onChangeSelection([widgetUnderPosition.id])
                }
            } else {
                const newSelection: Array<string> = []
                let found = false
                for (let i = 0; i < canvasSelection.length; i++) {
                    if (canvasSelection[i] !== widgetUnderPosition.id) {
                        newSelection.push(canvasSelection[i])
                    } else {
                        found = true
                    }
                }
                if (!found) {
                    newSelection.push(widgetUnderPosition.id)
                }
                onChangeSelection(newSelection)
            }
        } else if (!ctrlKey) {
            onChangeSelection([])
        }
    }

    private isWidgetUnderPosition(widgets: Array<Widget>,
                                  position: CanvasPosition,
                                  layerIds: Array<string>): Widget | undefined {
        const foundWidgets: Array<Widget> = []
        for (let i = 0; i < widgets.length; i++) {
            const widget = widgets[i]
            if (position.left >= widget.left
                && position.left <= (widget.left + widget.width)
                && position.top >= widget.top
                && position.top <= (widget.top + widget.height)) {
                foundWidgets.push(widget)
            }
        }
        if (foundWidgets.length === 0) {
            return undefined
        }
        const sortedWidgets = this.getSortedWidgets(foundWidgets, layerIds)
        return sortedWidgets[sortedWidgets.length - 1]
    }

    getSortedWidgets(widgets: Array<Widget>, layerIds: Array<string>): Array<Widget> {
        const rawLayersIds = layerIds
        const withShadowWidgets: Array<Widget> = widgets.map(widget => {
            // TODO: Implement after adding shadow widgets
            return widget
        })

        const filteredWidgets = withShadowWidgets.filter(widget => {
            if (rawLayersIds.length === 0 || !widget.layers || widget.layers.length === 0) {
                return true
            }
            const widgetLayers = widget.layers
            return rawLayersIds.some(layerId => layerId.length === 0 || widgetLayers.indexOf(layerId) !== -1)
        })

        return filteredWidgets.sort((a, b) => {
            const zDiff = a.zIndex - b.zIndex
            if (zDiff !== 0) {
                return zDiff
            }
            const yDiff = a.top - b.top
            if (yDiff !== 0) {
                return yDiff
            }
            return a.left - b.left
        })
    }

    deleteWidgets(canvasId: string, widgetIds: Array<string>) {
        messagesService.send<RemoveWidgets>('canvas.remove-widgets', {
            canvasId,
            widgetIds,
        })
    }

    duplicateWidgets(canvasId: string, widgetIds: Array<string>, allWidgets: Array<Widget>, alignToGrid = false) {
        const selectedWidgets = allWidgets.filter(w => widgetIds.includes(w.id))
        
        if (selectedWidgets.length === 0) {
            return
        }

        const maxRight = Math.max(...selectedWidgets.map(w => w.left + w.width))
        const minLeft = Math.min(...selectedWidgets.map(w => w.left))
        
        const offsetX = maxRight + 16 - minLeft

        const items = selectedWidgets.map(w => ({
            type: w.type,
            left: alignToGrid ? roundToGrid(w.left + offsetX) : w.left + offsetX,
            top: alignToGrid ? roundToGrid(w.top) : w.top,
            width: w.width,
            height: w.height,
            zIndex: w.zIndex,
            data: w.data,
            layers: w.layers,
            tags: w.tags
        }))

        messagesService.send<CreateWidgets>('canvas.new-widgets', {
            canvasId,
            widgets: items
        })
    }

    copyWidgets(widgetIds: Array<string>, allWidgets: Array<Widget>): string {
        const selectedWidgets = allWidgets.filter(w => widgetIds.includes(w.id))

        if (selectedWidgets.length === 0) {
            return ''
        }

        return selectedWidgets.map(w => {
            if (w.type === WidgetType.STICKY_NOTE) {
                return (w.data as StickyNoteData).text || '(empty)'
            }
            if (w.type === WidgetType.TEXT_WIDGET) {
                return (w.data as TextWidgetData).text || '(empty)'
            }
            if (w.type === WidgetType.SHAPE) {
                return (w.data as ShapeWidgetData).shapeType || '(shape)'
            }
            if (w.type === WidgetType.REACTION) {
                return (w.data as ReactionWidgetData).reactionType || '(reaction)'
            }
            if (w.type === WidgetType.MARKDOWN) {
                return (w.data as MarkdownWidgetData).text || '(empty)'
            }
            if (w.type === WidgetType.DIAGRAM) {
                return (w.data as DiagramWidgetData).text || '(empty)'
            }
            if (w.type === WidgetType.TABLE) {
                return (w.data as TableWidgetData).text || '(empty)'
            }
            return `Widget: ${w.type}`
        }).join('\n')
    }

    deleteWidgetLinks(canvasId: string, linkIds: Array<string>) {
        messagesService.send<RemoveWidgetLinks>('canvas.remove-widget-links', {
            canvasId,
            linkIds
        })
    }

    createFromTemplate(canvasId: string,
                       layerIds: Array<string>,
                       templateClickPosition: CanvasPosition,
                       template: Template,
                       params: TemplateItemParams,
                       alignToGrid = false) {
        messagesService.send<CreateWidgetFromTemplate>('canvas.create-from-template', {
            canvasId,
            templateId: template.id,
            layers: layerIds.join(', '),
            leftDiff: alignToGrid ? roundToGrid(templateClickPosition.left) : Math.floor(templateClickPosition.left),
            topDiff: alignToGrid ? roundToGrid(templateClickPosition.top) : Math.floor(templateClickPosition.top),
            params: params
        })
    }
}

export const canvasContentService = new CanvasContentService()
